import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Attribute,
  AttributeGroup,
  AttributeGroupType,
  AttributeProperty,
  ComponentProps,
} from "react-builder";
import { ButtonStateAttributes } from "react-builder";
import { ContainerAttributes } from "react-builder";
import { useProductApi } from "../../custom-hooks/useProductApi";
import { GameState, IQGameInterface, ResultIQGameInterface } from "../../types/iq-game-types";
import {
  buildInitPage,
  GameHeader,
  QuestionScreen,
  BuildResultsPage,
  buildError,
} from "./IQGameScreens";

export class IQCategory extends Attribute {
  @AttributeProperty({ type: "String" })
  title: string = "default";

  @AttributeProperty({ type: "Color", input: "color" })
  color: string = "#f00";
}

@AttributeGroup({ title: "IQGameV2", blockName: "IQGameV2", module: "iq" })
export class IQGameV2Attributes extends Attribute {
  @AttributeProperty({ type: "Image" })
  introThumb?: string;
  @AttributeProperty({ type: "Image" })
  introThumbTest?: string;

  @AttributeProperty({
    title: "Categories",
    arrayLabelProperty: "title",
    type: AttributeGroupType.List,
    arrayType: IQCategory,
  })
  categories: IQCategory[] = [
    new IQCategory().fromJSON({ title: "Logic", color: "#f38f20" }),
    new IQCategory().fromJSON({ title: "Math", color: "#00aeef" }),
    new IQCategory().fromJSON({ title: "Visual", color: "#2bb673" }),
    new IQCategory().fromJSON({ title: "Focus", color: "#ed1c24" }),
  ];

  @AttributeProperty({ type: "Object", cssTarget: "" })
  container: ContainerAttributes = new ContainerAttributes();

  @AttributeProperty({ type: "Object", cssTarget: " button.gameButton" })
  button: ButtonStateAttributes = new ButtonStateAttributes();
}

const IQGameV2Theme = styled.div(
  (props: { attributes: IQGameV2Attributes; themeColor: string }) => `
  ${props.attributes.toCSS()}

  .scores > *,
  .game-progress > :first-child  {
    display: flex;
    justify-content: space-between;
  }
  .game-container{
    flex-direction: column;
    width: 100%;
    
    .game-a-wrap{
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      display: grid;
      justify-items: center;
      grid-template-columns:  1fr 1fr 1fr 1fr; 
      gap: 10px; 
      @media screen and (max-width: 650px) {
        grid-template-columns: repeat(2, 1fr);
        }
      }
      
  }

    .game-pause .title h4, .game-progress .type {
        color: ${props.themeColor};
    }
    
    .game-progress{
      width: 100%;
    }
    .game-on{
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 24px;
         .icon {
            width: 80px;
            margin: auto;
        }
    }

    .game-q {
        width: 100%;
        height: 340px;
        margin: 20px 0;
        position: relative;
        border-radius: 6px;
        box-shadow: 0 8px 12px 0 rgb(0 0 0 / 10%), 0 0px 0 1px #f7f9fc;
        background: #fff;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        @media screen and (max-width: 768px) {
          height: 200px;
      }

         
    }

    .a-inner-wrap {
        overflow: hidden;
        position: relative;
        margin-right: 10px;
        user-select: none;
        box-shadow: inset 0 0 0 1px #c9ddf0;
        border-radius: 6px;
        transition: .2s ease;
        cursor: pointer;
        width: 140px;
      
        .num {
        background: ${props.themeColor};
        position: absolute;
        top: 0;
        left: 5px;
        width: 20px;
        height: 20px;
        border-radius: 0 0 4px 4px;
        text-align: center;
        line-height: 1.1;
        font-weight: 600;
        color: #fff;
    }

    .a-inner-wrap:hover {
        box-shadow: inset 0 0 0 1px ${props.themeColor}, inset 0 -3px 0 ${props.themeColor};
    }
    .a-inner-wrap:hover {
        border-radius: 10px;
    }

    .game-a {
        height: 120px;
    }

    .inner-progress{
        background: #c9ddf0;
        height: 10px;

        .memory {
            background: ${props.themeColor};
            height: 100%;
        }
    }

    

`
);

const initialGameState: GameState = {
  questions: [],
  currentQuestionIndex: 0,
  userAnswers: [],
};

function IQGameV2(props: ComponentProps<IQGameV2Attributes>) {
  const iqGameAPI = useProductApi<IQGameInterface>();
  const iqGameScoreAPI = useProductApi<ResultIQGameInterface>();
  const [loading, setLoading] = useState(true);
  const [IQGameObject, setIQGameObject] = useState<IQGameInterface>();
  const [time, setTime] = useState<number>(0);
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const [gameScore, setGameScore] = useState<ResultIQGameInterface>();
  const [gameOn, setGameOn] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const intervalRef = useRef<any>();

  useEffect(() => {
    const requestGame = async () => {
      if (!iqGameAPI) return;
      setLoading(true);
      try {
        const response = await iqGameAPI.post(`iqtest/IQGame`);
        if (response && response[0] && response[0].questions) {
          setIQGameObject(response);
          setGameState((gameState) => ({
            ...gameState,
            questions: [...response[0].questions],
          }));
        } else if ((response as any).message) setError((response as any).message);
      } catch (error) {
        console.error("Error fetching game:", error);
      } finally {
        setLoading(false);
      }
    };
    requestGame();
    // on component unmount clear timer interval
    return () => {
      clearInterval(intervalRef.current);
    };
  }, [iqGameAPI]);

  const getThemeColor = (): string => {
    if (
      !props.attributes ||
      !props.attributes.categories ||
      props.attributes.categories.length === 0
    ) {
      console.warn("Missing or empty categories in props.attributes");
      return "#313131";
    }

    if (
      !gameState ||
      !gameState.questions ||
      gameState.questions.length === 0 ||
      gameState.currentQuestionIndex >= gameState.questions.length
    ) {
      console.warn("Invalid gameState or no questions available");
      return "#313131";
    }

    const currentQuestion = gameState.questions[gameState.currentQuestionIndex];

    const foundCategory = props.attributes.categories.find(
      (category) => category.title.toLowerCase() === currentQuestion.category.toLowerCase()
    );

    if (!foundCategory) {
      console.warn(`No category found for question category "${currentQuestion.category}"`);
      return "#313131";
    }

    return foundCategory.color;
  };

  const startGame = () => {
    intervalRef.current = setInterval(() => {
      setTime((time) => time + 1);
    }, 1000);
    setGameOn(true);
  };

  const handleNext = (answer: string) => {
    if (gameState.currentQuestionIndex === gameState.questions.length - 1) {
      clearInterval(intervalRef.current);
      endGame([...gameState.userAnswers, answer]);
      setGameState((gameState) => ({
        ...gameState,
        userAnswers: [...gameState.userAnswers, answer],
      }));
      return;
    }
    setGameState((gameState) => ({
      ...gameState,
      currentQuestionIndex: gameState.currentQuestionIndex + 1,
      userAnswers: [...gameState.userAnswers, answer],
    }));
  };

  const endGame = async (answers: string[]) => {
    setGameOn(false);
    if (!iqGameScoreAPI) return;
    setLoading(true);
    try {
      const response = await iqGameScoreAPI.patch(
        `iqtest/IQGame/${IQGameObject?.[0]._id}/answers`,
        {
          answers,
        }
      );
      if (response.game) {
        setGameScore(response);
      }
    } catch (error) {
      console.error("Error fetching game:", error);
    } finally {
      setLoading(false);
    }
  };
  const showInitPage = !gameOn && gameState.currentQuestionIndex === 0;
  const showGame = gameOn && gameState.questions?.length > 0;
  const showResultsPage = !gameOn && gameScore;
  const showError = !(loading || showInitPage || showGame || showResultsPage);
  return (
    <IQGameV2Theme
      {...props}
      className={`iq-game-container ${props.className}`}
      themeColor={getThemeColor()}
    >
      {showInitPage && buildInitPage(props.attributes.introThumb, startGame)}

      {showGame && (
        <div className="game-container">
          <GameHeader
            colorTheme={getThemeColor()}
            categoryTitle={gameState.questions[gameState.currentQuestionIndex].category}
            time={time}
            currentQuestionIndex={gameState.currentQuestionIndex}
            totalCount={gameState.questions.length}
          />
          <QuestionScreen
            question={gameState.questions[gameState.currentQuestionIndex]}
            onNext={(answer) => handleNext(answer._id)}
          />
        </div>
      )}
      {showResultsPage && (
        <BuildResultsPage
          gameScore={gameScore.game.score}
          time={time}
          categories={props.attributes.categories}
          gameState={gameState}
        />
      )}
      {loading && <div>{props.children}</div>}
      {showError && buildError(error)}
    </IQGameV2Theme>
  );
}

IQGameV2.prototype.Props = IQGameV2Attributes;

export default IQGameV2;
